'use client';

import { ValidationError } from '@eliadmin/core/src/api/errors';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

type ActionVariables = {
  /** Core add to cart (`useAddToCart`). */
  body?: { item?: { product?: { id?: number } } };
  /** Core favourites (`addProductToFavorites`). */
  productSlug?: string;
};

/**
 * The core AddToCartButton and AddToFavoritesButton drop API errors silently. This watches the
 * shared mutation cache for a failed cart or favourite request of the product and shows the core
 * error toast – the API message for validation errors (out of stock, quantity), a generic text else.
 */
export function useProductActionErrors(product: { id: number; slug: string }) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslations();

  useEffect(
    () =>
      queryClient.getMutationCache().subscribe(event => {
        if (event.type !== 'updated' || event.action.type !== 'error') return;
        const variables = event.mutation.state.variables as ActionVariables | undefined;
        const own =
          variables?.body?.item?.product?.id === product.id ||
          variables?.productSlug === product.slug;
        if (!own) return;
        const { error } = event.action;
        const detail =
          error instanceof ValidationError && error.message !== ValidationError.statusText
            ? error.message
            : t('text1320');
        toast({ title: t('text1472'), description: detail, variant: 'destructive' });
      }),
    [queryClient, toast, t, product.id, product.slug],
  );
}
